import React, { useState, useCallback } from "react"
import { navigate } from 'gatsby'

import ColoredLink from 'components/common/colored-link/ColoredLink'
import Modal from 'components/common/modal/Modal'
import Button from 'components/forms/button/Button'
import Input from 'components/forms/Input/Input'
import MaskedInput from 'components/forms/MaskedInput/MaskedInput'

import { ReactComponent as CrossSvg } from 'resources/icons/general/cross_pt1.svg'
import { ReactComponent as SupportSvg } from 'resources/icons/etc/support_solid.svg'

import * as st from './TalkSales.styled.js'

async function sendRequest({
  firstName,
  lastName,
  email,
  companyName,
  phone,
}) {
  const response = await fetch("/api/p/forms/talk-sales", {
    headers: {"Content-Type": "application/json"},
    method: "POST",
    body: JSON.stringify({
      "first-name": firstName,
      "last-name": lastName,
      "email": email,
      "company-name": companyName,
      "phone": phone,
    }),
  })

  return response.json(); // parses JSON response into native JavaScript objects
}

const TalkSales = ({
  isOpen,
}) => {
  const onClose = useCallback(() => navigate(""), [])

  const [
    isProgress,
    setProgress
  ] = useState(false);

  const [
    invalidFields,
    setInvalidFields
  ] = useState([]);

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    phone: "",
  })

  const onChange = useCallback((prop, value) => {
    const newData = ({...data, [prop]: value})
    setData(newData)
    setInvalidFields(fields => fields.filter(f => f !== prop))
  }, [data, setData])

  const onSubmit = useCallback(async event => {
    event.preventDefault();

    setProgress(true)
    const invalidFields = [].concat(
      data.firstName.trim() === "" ? ["firstName"] : [],
      data.lastName.trim() === "" ? ["lastName"] : [],
      data.email.trim() === "" ? ["email"] : [],
      data.companyName.trim() === "" ? ["companyName"] : [],
    )
    setInvalidFields(invalidFields)

    if (invalidFields.length === 0) {
      await sendRequest(data);
      onClose();
    }
    setProgress(false)
  }, [data, setProgress, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Talk to sales modal"
      onClose={onClose}
    >
      <st.SidePanel>
        <st.SidePanelBackground />
        <img
          width={96}
          height={20}
          alt="Scope logo"
          src="//cdn-platform.scope-dev.ws/website/scope-logo-horizontal-white.png"
          srcSet="//cdn-platform.scope-dev.ws/website/scope-logo-horizontal-white.png 1x, //cdn-platform.scope-dev.ws/website/scope-logo-horizontal-white@2x.png 2x"
        />
        <st.SideSlogan>Powerful platform to accelerate onboarding cycle, improve efficiency, and boost customer engagement and retention</st.SideSlogan>
      </st.SidePanel>
      <st.Content>
        <st.Header>
          <st.Heading>Talk with sales team</st.Heading>
          <st.CloseButton
            view="simple"
            size={600}
            icon={CrossSvg}
            onClick={onClose}
          />
        </st.Header>
        <st.InnerContent>
          <st.Banner>
            <st.IconBlock>
              <SupportSvg />
            </st.IconBlock>
            <span>Please fill out the information below and our sales team will reach out as soon as possible.</span>
          </st.Banner>
          <st.Form
            name="talk-sales"
            onSubmit={onSubmit}
          >
            <st.FormContent>
              <st.Column>
                <st.InputField>
                  <label htmlFor="first-name">First Name</label>
                  <Input
                    id="first-name"
                    size={500}
                    view="outline"
                    value={data.firstName}
                    autoFocus /* eslint-disable-line jsx-a11y/no-autofocus */
                    isError ={invalidFields.indexOf("firstName") >= 0}
                    onChange={value => onChange("firstName", value)}
                  />
                </st.InputField>
                <st.InputField>
                  <label htmlFor="last-name">Last Name</label>
                  <Input
                    id="last-name"
                    size={500}
                    view="outline"
                    value={data.lastName}
                    isError={invalidFields.indexOf("lastName") >= 0}
                    onChange={value => onChange("lastName", value)}
                  />
                </st.InputField>
                <st.InputField>
                  <label htmlFor="email">Company Email</label>
                  <Input
                    id="email"
                    type="email"
                    size={500}
                    view="outline"
                    value={data.email}
                    isError={invalidFields.indexOf("email") >= 0}
                    onChange={value => onChange("email", value)}
                  />
                </st.InputField>
              </st.Column>
              <st.Column>
                <st.InputField>
                  <label htmlFor="company-name">Company Name</label>
                  <Input
                    id="company-name"
                    size={500}
                    view="outline"
                    value={data.companyName}
                    isError={invalidFields.indexOf("companyName") >= 0}
                    onChange={value => onChange("companyName", value)}
                  />
                </st.InputField>
                <st.InputField>
                  <label htmlFor="phone">Phone (optional)</label>
                  <MaskedInput
                    id="phone"
                    type="tel"
                    mask="+{1} (000) 000-0000"
                    unmask={false}
                    size={500}
                    view="outline"
                    value={data.phone}
                    placeholder="+1"
                    isError={invalidFields.indexOf("phone") >= 0}
                    onChange={value => onChange("phone", value)}
                  />
                </st.InputField>
              </st.Column>
            </st.FormContent>
            <st.Footer>
              <Button
                htmlType="submit"
                view="blue"
                size={600}
                isProgress={isProgress}
              >
                Send Request
              </Button>
            </st.Footer>
          </st.Form>
        </st.InnerContent>
      </st.Content>
    </Modal>
  )
}

export default TalkSales
